import { ChevronLeft, ChevronRight } from 'lucide-react';
import { memo } from 'react';
import {
  LoadingFooter,
  SlowLoadNotice,
  TableSkeletonBody,
  TopProgressBar,
  useTableLoading,
} from '../UI/loading';

export const PaginationMainTable = memo(function PaginationMainTable({
  children,
  pageSize,
  setPageSize,
  pageSizeOptions = [25, 50, 100],
  hasNext,
  hasPrev,
  onNextPage,
  onPreviousPage,
  isLoadingPage,
  loading = isLoadingPage,
  loadingLabel = 'Loading',
  onCancelLoading,
  cancelled = false,
  cancelledMessage = 'Search cancelled. Adjust filters and search again.',
  pageNo,
  totalCount,
  showCount = true,
}) {
  const { isLoading, showLoading, tableProps } = useTableLoading(loading, cancelled);
  const skeletonRows = pageSize > 0 && pageSize < 10 ? pageSize : 10;

  return (
    <div className="relative bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
      <TopProgressBar active={showLoading} />
      <div role="status" aria-live="polite" className="sr-only">
        {showLoading ? loadingLabel : ''}
      </div>
      <div className="w-full overflow-x-auto max-h-[600px] overflow-y-auto">
        <table
          className="aone-table min-w-full w-full text-sm"
          {...tableProps}
        >
          {children}
          {showLoading && <TableSkeletonBody rows={skeletonRows} />}
          {cancelled && !isLoading && (
            <tbody className="aone-skel-body">
              <tr>
                <td colSpan={100} className="px-6 py-12 text-center text-gray-500">
                  {cancelledMessage}
                </td>
              </tr>
            </tbody>
          )}
        </table>
      </div>

      <SlowLoadNotice active={isLoading} onCancel={onCancelLoading} className="m-3" />

      {showLoading ? (
        <LoadingFooter label={loadingLabel} />
      ) : (
        /* Pagination Controls Footer */
        <div className="h-[52px] border-t border-gray-200 border-solid border-b-0 border-x-0 bg-gray-50 px-6 flex items-center justify-end gap-6">
          {/* Page Size Selector */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Rows per page:</span>
            <select
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              className="text-xs border border-gray-300 rounded-md px-2 py-1 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-red-600 focus:border-transparent"
              disabled={isLoading}
            >
              {pageSizeOptions.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </div>

          {showCount && (
            <div className="text-xs text-gray-600">
              {totalCount > 0 ?
                `${pageNo * pageSize + 1} - ${Math.min(pageNo * pageSize + pageSize, totalCount)} of ${totalCount}` :
                null}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center gap-2">
            <button
              onClick={onPreviousPage}
              disabled={!hasPrev || isLoading}
              className="!p-1.5 rounded-md !border border-gray-300 border-solid bg-white hover:!bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              title="Go to previous page"
              aria-label="Go to previous page"
            >
              <ChevronLeft className="size-4 text-gray-600" />
            </button>

            <button
              onClick={onNextPage}
              disabled={!hasNext || isLoading}
              className="!p-1.5 rounded-md !border border-gray-300 border-solid bg-white hover:!bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent transition-colors"
              title="Go to next page"
              aria-label="Go to next page"
            >
              <ChevronRight className="size-4 text-gray-600" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
});
